import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { baseUrl } from "../../config/serverConfig";
import useAxios from "../../hooks/axios/useAxios";
import CreateOrderContext from "../createOrder";

/**
 * Eén regel van de calculatie, exact zoals de backend hem teruggeeft.
 *
 * Sinds rekenversie 2 is elke regel artikelnummer × aantal uit de prijslijst.
 * Alleen waar er echt geen artikel bestaat (PVC eigen kleur, speciale
 * stootborden, plateau, vaste kosten) is de bron "config" en het nummer null.
 * `code` is een groep: bij een draai staan er bijvoorbeeld drie regels
 * "profielen" (100, 120 en 140 cm).
 */
export interface PricingLine {
  code: string;
  bron: "artikel" | "config";
  artikelnummer: string | null;
  omschrijving: string;
  aantal: number;
  stukprijsCents: number;
  regeltotaalCents: number;
}

export interface PricingResponse {
  version: number;
  lines: PricingLine[];
  totalCents: number;
  warnings: string[];
  margin: number;
  /** Btw over de inkoopprijs. */
  purchaseVatCents: number;
  /** Het margebedrag in euro's, bij de gekozen marge. */
  marginCents: number;
  /** Advies-verkoopprijs voor de eindklant, inclusief btw. */
  salesInclVatCents: number;
}

interface PricingContextValue {
  pricing: PricingResponse | null;
  loading: boolean;
  /** Leesbare melding van de server; blokkeert het opslaan. */
  error: string | null;
  /** De configuratie die aan de server is gestuurd, voor het opslaan van de order. */
  config: Record<string, unknown>;
  /** Alle regels met deze code(s); leeg als ze niet in de calculatie zitten. */
  regels: (code: string | string[]) => PricingLine[];
}

const PricingContext = createContext<PricingContextValue>({
  pricing: null,
  loading: false,
  error: null,
  config: {},
  regels: () => [],
});

/** Wachttijd na de laatste wijziging voordat er opnieuw wordt gerekend. */
const DEBOUNCE_MS = 300;

/** Centen naar een Nederlands eurobedrag. */
export const euroCents = (cents: number): string =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(
    cents / 100
  );

/**
 * Haalt de prijs bij de backend op.
 *
 * De frontend rekent zelf niets meer uit: elk bedrag op het scherm komt uit
 * `POST /pricing/calculate`. Zolang er geen antwoord is, is er geen bedrag —
 * dat is bewust, want een oud bedrag naast een nieuwe keuze is misleidend.
 */
export function PricingProvider({ children }: { children: ReactNode }) {
  const order = useContext(CreateOrderContext);
  const { fetchData } = useAxios({ method: "post" });

  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // useAxios levert bij elke render een nieuwe functie; via een ref blijft het
  // effect afhankelijk van de configuratie en niet van de identiteit daarvan.
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;

  // Volgnummer: een traag antwoord op een oude configuratie mag een nieuwer
  // antwoord niet overschrijven.
  const volgnummer = useRef(0);

  const config = useMemo<Record<string, unknown>>(
    () => ({
      type: order.type.value,
      profileColor: order.profileColor.value,
      stairMaterial: order.stairMaterial.value,
      stairColor: order.stairColor.value,
      stairAmount: order.stairAmount.value,
      breedte: order.breedte.value,
      trapvorm: order.trapvorm.value,
      risersMode: order.risersMode.value,
      specialRiserId: order.specialRiserId.value,
      topConnectionCode: order.topConnectionCode.value,
      extrasLed: order.extrasLed.value,
      ledMode: order.ledMode.value,
      ledCustomText: order.ledCustomText.value,
      ledDimmer: order.ledDimmer.value,
      ledSensor: order.ledSensor.value,
      extrasPlateau: order.extrasPlateau.value,
      plateauMaterial: order.plateauMaterial.value,
      plateauSize: order.plateauSize.value,
      margin: order.margin.value,
    }),
    [
      order.type.value,
      order.profileColor.value,
      order.stairMaterial.value,
      order.stairColor.value,
      order.stairAmount.value,
      order.breedte.value,
      order.trapvorm.value,
      order.risersMode.value,
      order.specialRiserId.value,
      order.topConnectionCode.value,
      order.extrasLed.value,
      order.ledMode.value,
      order.ledCustomText.value,
      order.ledDimmer.value,
      order.ledSensor.value,
      order.extrasPlateau.value,
      order.plateauMaterial.value,
      order.plateauSize.value,
      order.margin.value,
    ]
  );

  const sleutel = JSON.stringify(config);

  useEffect(() => {
    // Zonder treden valt er niets te rekenen; de server zou 400 geven.
    if (!config.stairAmount || Number(config.stairAmount) <= 0) {
      setPricing(null);
      setError(null);
      setLoading(false);
      return;
    }

    let afgebroken = false;
    const mijnNummer = ++volgnummer.current;
    setLoading(true);

    const timer = setTimeout(async () => {
      const response = await fetchRef.current(
        `${baseUrl}/pricing/calculate`,
        config
      );

      if (afgebroken || mijnNummer !== volgnummer.current) return;

      setLoading(false);

      if (response?.error) {
        setPricing(null);
        // Bij een 4xx/5xx staat in `message` de axios-tekst ("Request failed
        // with status code 400"); de melding van de server zit in `data`.
        setError(
          response.data?.message ??
            response.message ??
            "De prijs kon niet worden berekend. Probeer het opnieuw."
        );
        return;
      }

      setError(null);
      setPricing(response.data as PricingResponse);
    }, DEBOUNCE_MS);

    return () => {
      afgebroken = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel]);

  const waarde = useMemo<PricingContextValue>(
    () => ({
      pricing,
      loading,
      error,
      config,
      regels: (code: string | string[]) => {
        const codes = Array.isArray(code) ? code : [code];
        return pricing?.lines.filter((l) => codes.includes(l.code)) ?? [];
      },
    }),
    [pricing, loading, error, config]
  );

  return (
    <PricingContext.Provider value={waarde}>{children}</PricingContext.Provider>
  );
}

export function usePricing(): PricingContextValue {
  return useContext(PricingContext);
}

export default PricingContext;
